/**
 * ConfigSandboxPage
 *
 * Admin page to configure the code-execution sandbox service for this org.
 * Reads/writes organisation/{org_id}/config/sandbox in Firestore via
 * the /admin/config/sandbox endpoints.
 *
 * Route: /settings/config/sandbox  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import Toggle from "../../components/shared/Toggle";
import { isDirty } from "../../utils/isDirty";

const EMPTY_FORM = {
  url:     "",
  enabled: false,
};

const ConfigSandboxPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("sandbox");
      if (data.configured) {
        const loaded = {
          url:     data.url     ?? "",
          enabled: data.enabled ?? false,
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load sandbox config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateServiceConfig("sandbox", form);
      setOriginalForm(form);
      showFeedback("Sandbox config saved.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save sandbox config.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageShell
      title="Sandbox Configuration"
      description="Configure the code-execution sandbox used by agent tools."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Sandbox URL
            </label>
            <Input
              type="url"
              placeholder="https://sandbox.example.com"
              value={form.url}
              onChange={(e) => setForm((prev) => ({ ...prev, url: e.target.value }))}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Enable Sandbox</p>
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">Runs agent code in an isolated execution container</p>
            </div>
            <Toggle
              checked={form.enabled}
              onChange={(v) => setForm((prev) => ({ ...prev, enabled: v }))}
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !isDirty(form, originalForm)}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigSandboxPage;
